<template>
  <div class="group-detail">
    <h1 class="common__main-title">Group Detail</h1>
    <div class="sub-title">edit</div>
    <div class="content-wrapper">
      <ValidationObserver ref="observer">
        <div class="main-content">
          <table v-if="group" class="detail-table">
            <tbody>
              <tr>
                <th>Tutor ID</th>
                <td>
                  {{ group.tutor.id }}
                </td>
              </tr>
              <tr>
                <th>Tutor name</th>
                <td>
                  {{ group.tutor.name }}
                </td>
              </tr>
              <tr>
                <th>Course Code</th>
                <td>
                  {{ group.courseCode }}
                </td>
              </tr>
              <tr>
                <th>Level</th>
                <td>
                  {{ group.level.name }}
                </td>
              </tr>
              <tr>
                <th>day of week</th>
                <td>
                  <div v-if="isConfirm">{{ group.dayOfWeek | weekday }}</div>
                  <ValidationProvider v-else rules="required" v-slot="{ errors }">
                    <SelectWeekdayInput v-model="group.dayOfWeek" />
                    <div class="error-text">{{ errors[0] }}</div>
                  </ValidationProvider>
                </td>
              </tr>
              <tr>
                <th>Period</th>
                <td>
                  <div v-if="isConfirm">{{ group.period }}</div>
                  <ValidationProvider v-else rules="required" v-slot="{ errors }">
                    <SelectPeriodInput v-model="group.period" />
                    <div class="error-text">{{ errors[0] }}</div>
                  </ValidationProvider>
                </td>
              </tr>
              <tr>
                <th>University</th>
                <td>
                  {{ group.university.name }}
                </td>
              </tr>
              <tr>
                <th>Campus</th>
                <td>
                  {{ group.campus.name }}
                </td>
              </tr>
              <tr>
                <th>Booth</th>
                <td>
                  <div v-if="isConfirm">{{ group.booth | booth }}</div>
                  <ValidationProvider v-else rules="required" v-slot="{ errors }">
                    <SelectBoothInput v-model="group.booth"
                      :university="group.university" :campus="group.campus" />
                    <div class="error-text">{{ errors[0] }}</div>
                  </ValidationProvider>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ValidationObserver>

      <table v-if="group" class="detail-table">
        <tbody>
          <tr>
            <th>Student</th>
            <td>
              <div class="student" v-for="student of group.students" :key="student.id">
                <router-link :to="{name: 'StudentDetail', params: {id: student.id}}"
                class="student-name">{{ student.name }} ({{ student.studentNumber }})</router-link>
                <b-button
                  v-if="group && group.fiscalYear <= 2023"
                  pill size="sm" variant="primary"
                  :to="{ name: 'OldLMSData' }">
                  Move
                </b-button>
                <b-button
                  v-else
                  pill size="sm" variant="primary"
                  :to="{ name: 'GroupStudentMove', params: {
                    groupId: group.id, studentId: student.id
                  } }"
                >
                  Move
                </b-button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-between">
        <template v-if="isConfirm">
          <b-button pill variant="secondary" @click="switchConfirm(false)">Back</b-button>
          <b-button pill variant="primary" @click="edit">Edit</b-button>
        </template>
        <template v-else>
          <b-button pill variant="secondary" :to="{ name: 'Groups' }">Back</b-button>
          <b-button pill variant="primary" @click="switchConfirm(true)">Comfirm</b-button>
        </template>
      </div>
    </div>

    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import _ from 'lodash'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { Group } from '@/store/groups/types'
import { weekdayKey, Weekdays } from '@/store/common.types'
import { Booth } from '@/store/booths/types'
import SelectWeekdayInput from '@/components/parts/inputItem/SelectWeekdayInput.vue'
import SelectPeriodInput from '@/components/parts/inputItem/SelectPeriodInput.vue'
import SelectBoothInput from '@/components/parts/inputItem/SelectBoothInput.vue'
import Loading from '@/components/parts/modal/Loading.vue'

export default Vue.extend({
  components: {
    SelectWeekdayInput,
    SelectPeriodInput,
    SelectBoothInput,
    Loading,
    ValidationProvider,
    ValidationObserver,
  },

  filters: {
    weekday: (w: weekdayKey) => Weekdays[w],
    booth: (b: Booth): string => `${b.building} - ${b.room} - ${b.number}`,
  },

  data: (): {
    group: Group|null,
    isConfirm: boolean,
    isLoading: boolean,
  } => ({
    group: null,
    isConfirm: false,
    isLoading: false,
  }),

  created() {
    this.isLoading = true
    this.$store.dispatch('getGroupById',
      { groupId: this.$route.params.groupId })
      .then(() => {
        this.isLoading = false
        this.group = _.cloneDeep(this.$store.getters.findGroupById(this.$route.params.groupId))
      })
  },
  methods: {
    /**
     * 確認画面モードに移行
     * @param {boolean} isConfirm true: 確認画面, false: 入力画面
     */
    async switchConfirm(isConfirm: boolean): Promise<void> {
      if (isConfirm) {
        const isNoProbrem = await (this.$refs.observer as any).validate()
        if (!isNoProbrem) {
          return
        }
      }

      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
      this.isConfirm = isConfirm
    },
    edit() {
      if (this.isLoading) {
        return
      }
      this.isLoading = true

      this.$store.dispatch('editGroup', {
        groupId: this.$route.params.groupId,
        group: this.group,
      })
        .then(() => {
          this.isLoading = false
          this.$bvModal.msgBoxOk('Success !')
            .then(() => { this.$router.go(0) })
        })
        .catch((error) => {
          this.isLoading = false
          const message = _.get(error, 'response.data.message') || error.message
          this.$bvModal.msgBoxOk(`Failed: ${message}`)
        })
    },
  },
})
</script>

<style scoped>
.sub-title {
  padding:10px;
  margin-bottom: 20px;
  background: #D8D8D8;
}

.content-wrapper {
  background: #D8D8D8;
  padding: 20px 50px;
}

.main-content {
  padding: 20px;
  border-radius: 20px;
  margin-bottom:20px;
}

table.detail-table {
  width:100%;
}

table.detail-table th {
  display: block;
  text-align: left;
  margin:10px 0;
}

table.detail-table td {
  display: block;
  margin:10px 0;
}

.student {
  margin-bottom:10px;
  align-items: center;
  display: flex;
}

.student-name {
  margin-right:20px;
}

input[type="text"], select{
  padding: 5px;
  border: 1px solid #AAAFB6;
  border-radius: 30px;
  width:100%;
}

</style>
